<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import type { components } from '@scalty/contracts'
import DropdownMenu from '~/components/ui/DropdownMenu.vue'
import Icon from '~/components/ui/Icon.vue'
import type { MenuGroup, MenuItem } from '~/components/ui/menu'
import { productVersionLabel } from '~/utils/productVersion'

const session = useSession()
const auth = useAuth()
const route = useRoute()
const open = ref(false)
const pending = ref(false)
const error = ref('')
const user = computed(() => session.value.data?.user)
const initials = computed(() => user.value?.name.trim().split(/\s+/).map(part => part.charAt(0)).join('').slice(0, 2).toUpperCase() || user.value?.email.charAt(0).toUpperCase() || 'U')
const { data: me, error: readError } = useQuery({
  queryKey: computed(() => ['studio-source', session.value.data?.session.activeOrganizationId ?? null, '/me']),
  enabled: computed(() => Boolean(user.value)),
  queryFn: () => apiFetch<components['schemas']['MeEnvelope']>('/me'),
  staleTime: 5000,
})
const organizations = computed(() => organizationMembershipOptions(me.value?.me ?? null))
const config = useRuntimeConfig()
const version = computed(() => productVersionLabel(config.public.localDevelopment ? 'local' : config.public.appEnv, config.public.releaseCommit))
const groups = computed<MenuGroup[]>(() => [
  { id: 'account', items: [
    { id: '/profile', label: 'Profile', icon: 'people', disabled: pending.value },
    { id: '/settings', label: 'Settings', icon: 'settings', disabled: pending.value },
    { id: '/org/current', label: 'Organisation settings', icon: 'overview', disabled: pending.value },
  ] },
  ...(organizations.value.length > 1 ? [{ id: 'organizations', label: 'Switch organisation', items: organizations.value.map(option => ({
    id: option.membership.organization.id, label: option.membership.organization.name, icon: 'overview' as const,
    active: option.active, disabled: option.active || pending.value,
  })) }] : []),
  { id: 'session', items: [{ id: 'logout', label: pending.value ? 'Please wait…' : 'Logout', icon: 'logout', destructive: true, disabled: pending.value }] },
])

watch(() => route.path, () => { open.value = false })
watch(open, value => { if (value) error.value = '' })

async function select(item: MenuItem, event: Event) {
  if (item.id.startsWith('/')) { await navigateTo(item.id); return }
  event.preventDefault()
  if (pending.value) return
  pending.value = true
  error.value = ''
  try {
    if (item.id === 'logout') {
      const result = await auth.signOut()
      if (result.error) throw new Error(result.error.message ?? 'Could not sign out. Please try again.')
      await navigateTo('/login')
    } else {
      const membership = switchableOrganizationMembership(me.value?.me ?? null, item.id)
      if (!membership) throw new Error('Select another organisation from your memberships.')
      await performActiveOrganizationSwitch(membership.organization.id, {
        request: (path, options) => apiFetch(path, options),
        storage: window.localStorage,
        fullNavigate: () => window.location.assign('/overview'),
      })
    }
  } catch (failure) {
    error.value = failure instanceof Error ? failure.message : 'The action failed. Please try again.'
    open.value = true
  } finally {
    pending.value = false
  }
}
</script>

<template>
  <DropdownMenu v-model:open="open" label="User menu" :groups="groups" side="top" @select="select">
    <template #trigger>
      <button
        type="button" aria-label="User menu"
        class="flex h-[var(--shell-topbar-height)] w-full shrink-0 items-center gap-2 border-t border-sidebar-border px-2.5 text-left text-sidebar-foreground outline-none hover:bg-sidebar-accent focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-ring data-[state=open]:bg-sidebar-accent"
      >
        <span class="grid size-[var(--measure-22px)] shrink-0 place-items-center rounded-full bg-muted text-[length:var(--type-fine)] font-semibold">{{ initials }}</span>
        <span class="min-w-0 flex-1 truncate text-xs" :title="user?.email">{{ user?.email }}</span>
        <Icon name="chevron-down" :size="11" class="shrink-0 text-muted-foreground" :class="open && 'rotate-180'" />
      </button>
    </template>
    <p v-if="error || readError" role="alert" class="px-[var(--menu-item-inset)] py-1.5 text-xs text-destructive">{{ error || readError?.message }}</p>
    <template v-if="version" #footer><span data-product-version>{{ version }}</span></template>
  </DropdownMenu>
</template>
